/**
 * Metric definition with label and unit
 */
export interface MetricDefinition {
  key: string;
  label: string;
  unit: string;
}

/**
 * Request body for exporting metrics
 */
export interface ExportMetricsRequest {
  deviceIds: string[];
  metrics: string[];
  startDate: string;
  endDate: string;
}

/**
 * Response from available metrics endpoint
 */
interface AvailableMetricsResponse {
  metrics: MetricDefinition[];
}

/**
 * Export Service
 * Handles metrics export functionality
 */
export const exportService = {
  /**
   * Get all available metrics with their metadata
   */
  async getAvailableMetrics(): Promise<MetricDefinition[]> {
    const response = await fetch('/api/metrics/available');

    if (!response.ok) {
      throw new Error(`Failed to fetch available metrics: ${response.statusText}`);
    }

    const data: AvailableMetricsResponse = await response.json();
    return data.metrics;
  },

  /**
   * Export metrics data to CSV file
   * Triggers browser download automatically
   */
  async exportMetrics(request: ExportMetricsRequest): Promise<void> {
    const response = await fetch('/api/metrics/export', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to export metrics');
    }

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'metrics_export.csv';

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Create blob and trigger download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
};

export default exportService;
